// A teacher's HOTS sitting: the thinking questions for one sub-topic, to look through.
//
// Separate from HotsPlayer rather than a role branch inside it, because almost everything the
// student flow carries is absent here: nothing is saved, no coins are awarded, no Smart XP is
// reported, and there is no start popup. What is left is the reverse - free movement between
// questions, which a child does not get.
//
// SELECTION: getHotsForTeacher resolves against the teacher's own CURRICULUM skills with no
// attempt history, so it returns everything the sub-topic has. A teacher has no mastered-skill
// list and no attempts to filter by, which is why the student's getHots cannot be used here -
// it would resolve to "none" and serve nothing.
//
// WHAT IS WRITTEN. One adaptive_questions row per question, the FIRST time the teacher answers
// it, so we can see whether teachers use this at all - plus the summary and daily achievement
// rows, which is exactly what a teacher preview of adaptive practice already writes. Withheld:
// hots_coins (an award, and adaptive practice has no equivalent surplus), the journey `mode`,
// question_levels calibration and Smart XP. The server decides all of it from the role on the
// token rather than trusting this screen.
//
// The teacher is never told about it. There is no saving indicator and no scored feel: an
// already-answered question simply comes back the way a review does.

import { AdaptivePracticeContext, AppContext } from '../../../context/context';
import { getTimeSpent } from '../../../utils/utils';
import { Component } from 'react';
import { ContentFactoryImpl, Content } from '@mathbuddy/adaptive-practice-engine';
import { PlayFrame } from '../frame/PlayFrame';
import { ls } from '../../../localStorage';
import apis from '../../../apis/api';
import { applyTheme, Theme } from '../theme';

class HotsForTeachers extends Component<any, any> {
  static contextType = AppContext;
  context!: { windowHeight: string; windowWidth: string; messageForParent: any; navigateTo: any };

  /** Two tries per question, as the child gets - the hint and explanation are part of what a
   *  teacher is here to look at. */
  static MAX_ATTEMPTS = 2;

  constructor(props: any) {
    super(props);
    this.state = {
      theme: Theme.Blueprint,
      contentFactoryObj: new ContentFactoryImpl(),
      contentObj: new Content(),
      langObj: { number_lang: 'en', lang: '' },
      serve: [],
      position: 0,
      // No start popup for a teacher, so the load begins immediately.
      isLoader: true,
      response: { component: [] },
      startTime: new Date(),
      isFinished: false,
      // The sub-topic has no HOTS at all. Distinct from isFinished: nothing was worked through,
      // so the ending popup must say so rather than report a set of zero as complete.
      isEmpty: false,
      answered: 0,
      correct: 0,
      // Which questions have been scored already. A teacher can move back to a question and
      // answer it again; only the FIRST attempt counts, so the tally reflects what they made of
      // the content rather than how many times they revisited it.
      scoredIds: [] as string[],
      // Questions already on record for this teacher, so a second sitting does not write the
      // same row twice. Seeded from the engine, which knows what they have attempted.
      savedIds: [] as string[],
      attempts: [],
      // No batch break for a teacher: the whole set is on screen from the start and they move
      // through it as they like, so the only pause is the close confirmation.
      pausePrompt: null as null | 'close',
    };
  }

  componentDidMount = () => {
    applyTheme(this.state.theme);
    this.loadSitting();
  };

  componentWillUnmount() {
    if (this.advanceTimer) clearTimeout(this.advanceTimer);
  }

  loadSitting = async () => {
    try {
      this.setState({ isLoader: true });
      const { currentUser, subTopicSkills, grade, lang } = this.props;
      const countryCode = (
        currentUser.classroom_country_code ||
        currentUser.country_code ||
        'IN'
      ).toLowerCase();
      const langObj = { number_lang: 'en', lang: `${lang}_${countryCode}` };

      const userObj = {
        userId: currentUser.id,
        classroomId: currentUser.classroom_id,
        dbToken: ls.getDBToken(),
        token: ls.getToken(),
        env: process.env.REACT_APP_ENV || '',
      };

      // The teacher variant: curriculum skills, no attempt history, whole set back. Already
      // ordered by the engine - nothing here should re-sort it.
      // `attempted` is what the teacher already has rows for. The whole set still comes back in
      // `serve` - a teacher is never sent a shorter list because they answered something - it
      // only says which ones must not be written again.
      // curriculumId is set for a Rise teacher, who has no classroom unit plan: the engine then
      // takes the curriculum's skills for `grade` from curriculumModulesByGrade instead. Absent,
      // it resolves the classroom's unit plan as it always has.
      const { serve, attempted } = await this.state.contentObj.getHotsForTeacher(
        userObj,
        subTopicSkills,
        grade,
        this.props.curriculumId,
      );

      if (!serve || serve.length === 0) {
        // The launcher asks the same index this does, so it normally does not offer a sub-topic
        // with nothing in it - but the two run in different places, and a teacher who gets here
        // should be told plainly rather than shown an empty set reported as finished.
        this.setState({ langObj, isLoader: false, isFinished: true, isEmpty: true });
        return;
      }

      this.setState(
        { langObj, serve, position: 0, savedIds: Array.isArray(attempted) ? attempted : [] },
        this.renderCurrent,
      );
    } catch (error) {
      // Nothing to fall back to, and a teacher should not be shown a technical failure any more
      // than a child. Close back to where they came from.
      console.log('hots (teacher): could not load the sitting', error);
      this.onClose();
    }
  };

  /** Render whichever question `position` points at. */
  renderCurrent = async () => {
    try {
      this.setState({ isLoader: true });
      const current = this.state.serve[this.state.position];
      // The engine paints its own half of the screen from themes/*.json, chosen by setTheme.
      // loadHotsQuestionById takes no theme argument, so it is set here or the engine keeps
      // whatever the last screen left in its module-level global.
      this.state.contentObj.setTheme(this.state.theme);
      const loaded = this.state.contentObj.loadHotsQuestionById(current.id);
      if (!loaded) {
        console.log('hots (teacher): unknown question id', current.id);
        this.next();
        return;
      }

      const response = await this.state.contentFactoryObj.displayQuestion(
        this.state.contentObj,
        this.state.contentObj.createContentTemplateObject(loaded.id, 'REACT_WEB'),
        this.state.langObj,
      );

      this.setState({
        response,
        targetQuestionId: loaded.id,
        isLoader: false,
        startTime: new Date(),
        attempts: [],
      });
    } catch (error) {
      console.log('hots (teacher): could not render question', error);
      this.next();
    }
  };

  /** Jump straight to a question. This is what the frame's prev/next buttons call, and it is
   *  the one thing a teacher can do that a child cannot. Free movement, so no block check -
   *  the prompt belongs to working through the set in order. */
  goTo = (position: number) => {
    if (position < 0 || position >= this.state.serve.length) return;
    this.setState({ position }, this.renderCurrent);
  };

  next = () => {
    const position = this.state.position + 1;
    if (position >= this.state.serve.length) {
      this.setState({ isFinished: true, isLoader: false });
      return;
    }

    this.setState({ position }, this.renderCurrent);
  };

  private advanceTimer?: ReturnType<typeof setTimeout>;

  /** Let the answer feedback play before moving on - the frame starts a sound the moment an
   *  answer is checked, and advancing immediately cuts it off. */
  advanceAfterFeedback = () => {
    this.advanceTimer = setTimeout(this.next, 1700);
  };

  saveQuestionResponse = async (
    answerObj: { givenAnswer: any; actualAnswer: any; isCorrectAns: Boolean },
    clickCount: number,
  ) => {
    const isCorrect = answerObj.isCorrectAns === true;

    // Same shape the child's row carries, `answer` included - the row is written now, and
    // play-service requires every attempt to name what was actually answered.
    const attempts = [
      ...this.state.attempts,
      { isCorrect, answer: answerObj.givenAnswer },
    ];
    const isLastTry = attempts.length >= HotsForTeachers.MAX_ATTEMPTS;
    const isQuestionOver = isCorrect || isLastTry;

    if (!isQuestionOver) {
      this.setState({ attempts });
      return;
    }

    // Correct advances on its own; wrong leaves the explanation up with its Next button, which
    // is the whole point of a teacher looking through these.
    const advanceNow = isCorrect;

    // Scored ONCE. Revisiting a question through the prev/next buttons lets the teacher answer
    // it again, but the tally must not move - otherwise the count says how often they clicked
    // rather than what the content is like.
    const id = this.state.targetQuestionId;
    const alreadyScored = this.state.scoredIds.includes(id);

    // Written ONCE per question, ever - not once per sitting. savedIds holds what the teacher
    // already has rows for, seeded from the engine at load and added to here, so re-answering a
    // question they came back to writes nothing. The server refuses anything beyond the row.
    if (!this.state.savedIds.includes(id)) {
      void this.saveFirstAttempt(id, attempts, isCorrect);
    }

    this.setState(
      (prev: any) => ({
        answered: alreadyScored ? prev.answered : prev.answered + 1,
        correct: alreadyScored ? prev.correct : prev.correct + (isCorrect ? 1 : 0),
        scoredIds: alreadyScored ? prev.scoredIds : [...prev.scoredIds, id],
        savedIds: prev.savedIds.includes(id) ? prev.savedIds : [...prev.savedIds, id],
        attempts: [],
      }),
      advanceNow ? this.advanceAfterFeedback : undefined,
    );
  };

  /**
   * One adaptive_questions row, so we can see whether teachers use this. Same endpoint and same
   * shape the child's sitting posts - the server tells the two apart by the role on the token
   * and writes nothing else for a teacher: no coins, no summary, no daily achievement, no
   * question_levels calibration.
   *
   * Fire-and-forget, and failures are swallowed. The teacher is not told this happens, so an
   * error here must not interrupt what they are doing - they would have no idea what it meant.
   */
  saveFirstAttempt = async (id: string, attempts: any[], isCorrect: boolean) => {
    try {
      await apis._saveHotsQuestionResponse({
        questions: [
          {
            user_id: this.props.currentUser.id,
            classroom_id: this.props.currentUser.classroom_id,
            // Stands where lo_id would on a skill question. It buckets question_levels for a
            // child; for a teacher nothing reads it, but the row shape is the same one.
            grade: Number(this.props.grade) || 0,
            question_id: id,
            review_obj: this.state.response.reviewObj,
            number_lang: this.state.langObj.number_lang,
            lang: this.state.langObj.lang,
            // No streak either way - the same freeze the child's row carries.
            isStreakFreeze: true,
            answers: attempts,
            isCorrect,
            blooms: this.state.response.blooms,
            learningGaps: this.state.response.learningGaps || [],
            time_spent: getTimeSpent(this.state.startTime),
          },
        ],
      });
    } catch (error) {
      console.log('hots (teacher): could not record the attempt', error);
    }
  };

  /**
   * The frame's one entry point for "show or dismiss the popup". It arrives from the close
   * icon, from the popup's Continue (as a click EVENT, so this toggles rather than reading its
   * argument), and from the Finish button on the last explanation.
   */
  setShowAdaptivePopup = () => {
    if (this.state.isFinished) {
      this.onClose();
      return;
    }

    // Finish: every question has been looked at, so end rather than ask about leaving.
    if (this.state.serve.length > 0 && this.state.answered >= this.state.serve.length) {
      this.setState({ isFinished: true, isLoader: false, pausePrompt: null });
      return;
    }

    // Continue on the close confirmation: the question was left on screen, so there is nothing
    // to draw again.
    if (this.state.pausePrompt) {
      this.setState({ pausePrompt: null });
      return;
    }
    this.setState({ pausePrompt: 'close' });
  };

  onClose = () => {
    // Nothing to report on the way out: attempt rows are written as they happen, and there are
    // no coins, no Smart XP and no summary to settle up.
    this.context.messageForParent('close');
  };

  render() {
    const total = this.state.serve.length;
    const attempted = Math.min(this.state.answered, total);
    const onQuestion = this.state.isFinished
      ? total
      : Math.min(this.state.position + 1, total);
    const accuracy = this.state.answered
      ? Math.round((this.state.correct * 100) / this.state.answered)
      : 0;

    return (
      <>
        <AdaptivePracticeContext.Provider value={{
          notificationMessage: '',
          // No coins and no streak for a teacher, exactly as the rest of the teacher play
          // screens do it.
          coins: undefined,
          streaks: undefined,
          isSafeZone: false,
          isLastQuestion: this.state.position >= total - 1,
          // The label names the question on screen; the bar reports what has been answered.
          meter: {
            percentage: total ? Math.round((attempted * 100) / total) : 0,
            level: total ? `${onQuestion} of ${total}` : '',
          },
          isLoader: this.state.isLoader,
          isCoinAnimation: false,
          isShowStartPopup: false,
          isShowAdaptivePopup: this.state.isFinished || this.state.pausePrompt !== null,
          isCloseFromClick: this.state.pausePrompt !== null,
          engineObj: this.state.response,
          options: {
            isToggleFullSceren: this.props.platform !== 'app',
            // No reload. Adaptive practice regenerates a question's numbers on reload, so the
            // button gives a child a fresh version of the same item; a HOTS is an authored,
            // static question, so there is nothing to regenerate - it would come back identical.
            isShowReload: false,
            isMusicAllowed: true,
            showFinishOnLast: true,
            // Free movement through the set - the teacher-only affordance.
            questionSummaryHeader: {
              current: this.state.position,
              total,
              setCurrentPos: this.goTo,
            },
          },
          loadContent: this.next,
          saveQuestionResponse: this.saveQuestionResponse,
          getPopUpInfo: () =>
            this.state.pausePrompt && !this.state.isFinished
              ? {
                  // A teacher is looking through content, not working towards anything, so the
                  // wording asks rather than warns - and counts what they have ANSWERED, since
                  // moving past a question without answering is a normal thing to do here.
                  heading: `${total - attempted} of ${total} not answered yet. Close anyway?`,
                  showAnimation: false,
                  totalAttemptedQuestions: this.state.answered,
                  accuracy,
                  completedStatusPercentage: total
                    ? Math.round((attempted * 100) / total)
                    : 0,
                  isShowStartPopup: false,
                  isCloseFromClick: true,
                  stopBtnText: 'Close',
                  // The default is "Continue Playing", which is not what a teacher is doing.
                  continueBtnText: 'Keep looking',
                }
              : this.state.isEmpty
              ? {
                  heading: 'No HOTS for this sub-topic yet',
                  // Nothing was attempted, so there is no result to report and the progress bar
                  // would sit at zero as if the teacher had failed at something.
                  hideStats: true,
                  showAnimation: false,
                  totalAttemptedQuestions: 0,
                  accuracy: 0,
                  completedStatusPercentage: 0,
                  isShowStartPopup: false,
                  isCloseFromClick: false,
                  stopBtnText: 'Done',
                }
              : {
                  heading: `That is all ${total} HOTS for this sub-topic.`,
                  // No celebration for a teacher - there is nothing being achieved here.
                  showAnimation: false,
                  totalAttemptedQuestions: this.state.answered,
                  accuracy,
                  completedStatusPercentage: 100,
                  isShowStartPopup: false,
                  isCloseFromClick: false,
                  stopBtnText: 'Done',
                },
          setShowStartPopup: () => {},
          setShowAdaptivePopup: this.setShowAdaptivePopup,
          onclose: this.onClose,
        } as any}>
          <PlayFrame></PlayFrame>
        </AdaptivePracticeContext.Provider>
      </>
    );
  }
}

export default HotsForTeachers;
